import {
  Users,
  DollarSign,
  HeartPulse,
  FileSignature,
  ShieldCheck,
  CalendarCheck,
  ClipboardList,
  Building2,
  ListChecks,
  Pill,
  Activity,
  UserPlus,
  Headset,
  MessageSquare,
  Lock,
  Wrench,
  Truck,
  type LucideIcon,
} from "@/lib/public-icons";
import { LANDING_FAQS, type MarketingFaq } from "@alm/domain/marketing/faqs";

const caregiverImg = "/marketing/caregiver_resident.png";
const adminDeskImg = "/marketing/admin_desk.jpg";
const techCareImg = "/marketing/tech_care.jpg";
const careTeamImg = "/marketing/care_team.png";

/* Journey order: sales → admission → care → pharmacy → billing. The four
   image spotlights walk the same sequence the landing tiles do. */
interface Spotlight {
  eyebrow: string;
  title: string;
  description: string;
  bullets: string[];
  image: string;
  icon: LucideIcon;
  /* The ask that belongs to this spotlight, if the spotlight resolves an objection a reader can act
   * on without booking anything. Optional on purpose: three of the four end in "book a demo", which
   * is what the band at the foot of the page is for. */
  cta?: {
    label: string;
    href: string;
    /** The terms of THIS step, said before it is taken. */
    note: string;
    testId: string;
  };
}

export const spotlight: Spotlight[] = [
  {
    eyebrow: "Fill the building",
    title: "Sales and admissions",
    description:
      "From first call to signed packet: a visual lead pipeline with tours, campaigns and referral sources, an AI front desk answering the phone, then e-signed admission packets and a Move-In Command Center whose steps verify themselves as the real work lands.",
    bullets: [
      "Lead pipeline, tours and conversion analytics",
      "E-signed admission packets, ask-once intake",
      "Move-In Command Center (auto-verified steps)",
      "Medicaid pathways like STAR+PLUS",
    ],
    image: careTeamImg,
    icon: HeartPulse,
  },
  {
    eyebrow: "Care at the center",
    title: "Resident and care management",
    description:
      "Every resident gets a complete, secure record: profiles, care plans with signing teams, vitals with pain assessment, activities, incidents, safety flags and a FaceSheet that pulls it all together. Therapy EMR charts PT, OT and ST against the same chart.",
    bullets: [
      "Care plans with e-signing teams",
      "Vitals, pain assessment and trends",
      "Incident and activity tracking",
      "Therapy EMR with SOAP notes and co-signing",
    ],
    image: caregiverImg,
    icon: Users,
  },
  {
    eyebrow: "Medication, mastered",
    title: "eMAR, med pass and pharmacy ops",
    description:
      "A full electronic MAR: physician orders, scheduled med passes with tap-to-administer, documented holds and refusals, and automatic late/missed tracking, plus pharmacy receiving with packing-slip scanning, refill workflows and supply run-out forecasting.",
    bullets: [
      "Tap-to-administer med pass",
      "MAR entries signed and timestamped",
      "Pharmacy receiving and refill requests",
      "Supply run-out forecasting",
    ],
    image: techCareImg,
    icon: Pill,
    /* Is Medications a real eMAR? A reader can check it in two minutes, so the call to action goes
     * straight to the med pass. */
    cta: {
      label: "Run this morning's med pass",
      href: "/experience",
      note: "The pass itself, in your browser, on sample data. No account, no sign-up, nothing to install.",
      testId: "features-emar-experience",
    },
  },
  {
    eyebrow: "Money, handled",
    title: "Billing and online payments",
    description:
      "Charges roll into invoices, and money comes back on its own: families pay online by ACH or card, autopay runs monthly, and paper payments are recorded too. Rent roll, AR aging, collections, expenses, P&L and trust fund reports are built in.",
    bullets: [
      "Online family payments (ACH and card)",
      "Autopay that runs itself",
      "Rent roll, AR and collections reports",
      "Expenses, P&L and trust fund",
    ],
    image: adminDeskImg,
    icon: DollarSign,
  },
];

/* Everything else the platform does, each tagged with the journey stage it
   belongs to. Rendered through byJourney(), so the grid cannot drift out of
   business-journey order however the entries are typed in here. */
export const moduleGrid = [
  {
    stage: "hiring",
    icon: UserPlus,
    title: "Hiring, HR and training",
    description:
      "A hiring pipeline with job postings, message and offer templates, HR document management, and training courses that issue certificates.",
  },
  {
    stage: "hiring",
    icon: CalendarCheck,
    title: "Staff scheduling",
    description:
      "Build the schedule, see who is on today, and keep assignments straight across every community.",
  },
  {
    stage: "sales",
    icon: Headset,
    title: "AI front desk",
    description:
      "An AI receptionist answers the phone, qualifies inquiries, texts callers back and escalates what matters to your team.",
  },
  {
    stage: "sales",
    icon: MessageSquare,
    title: "Fax and SMS built in",
    description:
      "A fax line and SMS number per facility: send charts and referrals straight from the record, text families and applicants from a shared inbox.",
  },
  {
    stage: "admission",
    icon: FileSignature,
    title: "Document e-signature",
    description:
      "ESIGN/UETA-aligned admission packets and templates. Sign in person or send a secure remote link; signed documents file into the chart.",
  },
  {
    stage: "care",
    icon: Users,
    title: "Family, physician and partner portals",
    description:
      "Families pay and sign online, physicians review and sign orders remotely, home health and hospice partners chart their own residents.",
  },
  {
    stage: "care",
    icon: ClipboardList,
    title: "Shift handoff board",
    description:
      "A tablet kiosk for the nurses' station: shift-change ceremony, PIN-signed actions and a live board of what needs doing.",
  },
  {
    stage: "care",
    icon: Activity,
    title: "Provider directory and PCP visits",
    description:
      "Centralized provider and institution directories with PCP visit tracking and problem lists.",
  },
  {
    stage: "billing",
    icon: Building2,
    title: "Multi-facility reporting",
    description:
      "Data isolation, role-based access and an All Facilities view with per-page facility overrides and consolidated reporting.",
  },
  {
    stage: "compliance",
    icon: ShieldCheck,
    title: "Medicaid / MCO tracking",
    description:
      "Authorization management per resident with payer info, modifiers, level of care and an Expiring Auths report.",
  },
  {
    stage: "compliance",
    icon: ListChecks,
    title: "Daily Roll and checklists",
    description:
      "Exception-based attendance with a monthly grid, plus admission and compliance checklists tracked per resident.",
  },
  {
    stage: "compliance",
    icon: Lock,
    title: "Security and backups",
    description:
      "HIPAA-compliant access control, PIN quick sign-in on facility devices, automatic idle sign-out and hourly encrypted off-site backups.",
  },
  {
    stage: "maintenance",
    icon: Wrench,
    title: "Tasks, notes and @mentions",
    description:
      "A cross-facility task center with Kanban and list views, a notes hub, and @mentions that notify teammates in context.",
  },
  {
    stage: "maintenance",
    icon: Truck,
    title: "Transport and vendors",
    description:
      "Resident transport scheduling plus vendor and expense management, so the non-clinical work is tracked like everything else.",
  },
] as const;

/* shared/marketingFaqs-v1.ts is read only by this arm (the live site reads the unsuffixed file), so
 * the product name is fixed at the source. The whole list goes to crawlers; the page shows the
 * first four and links /pricing and /security. */
export const OPERATING_SYSTEM_QUESTIONS: MarketingFaq[] = LANDING_FAQS;
